/**
 * Trusscore Estimator - Panel Calculations
 *
 * Calculates W&CB panel requirements for both horizontal and vertical orientations.
 * Includes optimization logic for minimizing waste.
 *
 * Ported from:
 * - calculateSurfaceWCBAndHTrims_Helper()
 * - findOptimalPanelLengthAndFits_Helper()
 * - calculateVerticalPanelMix_Helper()
 */

import { MaterialItem, ProductColor, CoreDimensions, GarageConfig, LengthTableEntry } from '../types';
import { getOptimalPanels, getVerticalOptimalPanels } from '../data/lengthTable';
import { getWCBProduct, WCB_PANEL_LENGTHS } from '../data/productTable';
import {
  PANEL_WIDTH_INCHES,
  PANEL_LENGTHS,
  MAX_PANEL_LENGTH,
  calculatePanelStripsForWidth,
} from './constants';

// ============================================================================
// HORIZONTAL PANEL CALCULATIONS
// ============================================================================

export interface HorizontalPanelResult {
  panels: MaterialItem[];
  hTrims: MaterialItem[];
  totalPanelCount: number;
  totalJoints: number;
}

/**
 * Calculate W&CB panels for a horizontal surface (ceiling or horizontal wall).
 *
 * This is a port of calculateSurfaceWCBAndHTrims_Helper from estimator.js
 *
 * @param surfaceLength - Length of the surface in feet
 * @param surfaceHeight - Height/width of the surface in feet
 * @param color - Panel color
 * @param garageConfig - Optional garage door configuration for wall surfaces
 * @param surfaceName - Name for logging/debugging
 * @returns Panels and H-trims needed
 */
export function calculateHorizontalPanels(
  surfaceLength: number,
  surfaceHeight: number,
  color: ProductColor,
  garageConfig?: GarageConfig | null,
  surfaceName?: string
): HorizontalPanelResult {
  const panels: MaterialItem[] = [];
  const hTrims: MaterialItem[] = [];

  if (surfaceLength <= 0 || surfaceHeight <= 0) {
    return { panels, hTrims, totalPanelCount: 0, totalJoints: 0 };
  }

  // Calculate number of horizontal rows (each panel is 16" high when horizontal)
  const numPanelRows = calculatePanelStripsForWidth(surfaceHeight);

  // OPTIMIZATION: For short widths (< 8'), use multi-row cutting optimization
  // Instead of 1 panel per row, calculate how many cuts fit per panel
  // Example: 4' width × 6 rows = 6 cuts needed
  //          An 8' panel yields 2 × 4' cuts, so need 3 panels (3 × 8' = 24' material)
  const MIN_PANEL_LENGTH = 8;

  if (surfaceLength < MIN_PANEL_LENGTH) {
    // Calculate total cuts needed (one per row)
    const totalCutsNeeded = numPanelRows;

    // Find optimal panel length that minimizes TOTAL material used
    const [optimalPanelLength, physicalPanelsNeeded] = findOptimalPanelForTotalCuts(
      surfaceLength,
      totalCutsNeeded
    );

    if (optimalPanelLength && physicalPanelsNeeded > 0) {
      const product = getWCBProduct(optimalPanelLength as 8 | 10 | 12 | 14 | 16 | 20, color);
      if (product) {
        panels.push({
          productNumber: product.productNumber,
          productID: product.productID,
          name: product.name,
          color: product.color as ProductColor,
          quantity: physicalPanelsNeeded,
          lengthFeet: product.lengthFeet,
          sqft: product.sqft,
          type: 'WCB_Surface',
        });
      }

      // No H-trims needed for short widths (no joints within each row)
      return {
        panels,
        hTrims,
        totalPanelCount: physicalPanelsNeeded,
        totalJoints: 0,
      };
    }
  }

  // Standard calculation for surfaces >= 8' (or fallback)
  // Look up optimal panel combination for the surface length
  const lengthEntry = getOptimalPanels(Math.ceil(surfaceLength));

  // Create panel items for each length type
  for (const panelLength of WCB_PANEL_LENGTHS) {
    const count = lengthEntry[String(panelLength) as keyof LengthTableEntry] as number;
    if (count > 0) {
      const product = getWCBProduct(panelLength, color);
      if (product) {
        const totalQuantity = count * numPanelRows;

        if (totalQuantity > 0) {
          panels.push({
            productNumber: product.productNumber,
            productID: product.productID,
            name: product.name,
            color: product.color as ProductColor,
            quantity: totalQuantity,
            lengthFeet: product.lengthFeet,
            sqft: product.sqft,
            type: 'WCB_Surface',
          });
        }
      }
    }
  }

  // Calculate H-trims for joints between panels
  // H-trim runs VERTICALLY at each joint, spanning the full surface height ONCE
  // (not multiplied by numPanelRows - the H-trim spans all rows at each joint location)
  //
  // Example: 40' wall × 10' height
  // - Uses 2× 20' panels per row (1 joint)
  // - Has 8 rows of panels (10' ÷ 1.33' panel width)
  // - H-trim runs vertically at the 20' mark, spanning the full 10' height
  // - Total H-trim needed: 1 joint × 10' height = 10' (1× 10' H-trim piece)
  if (lengthEntry.joints > 0) {
    const hTrimProduct = getHTrimProduct(color);
    if (hTrimProduct) {
      // H-trim length needed = number of joints × surface height (spans all rows)
      const totalHTrimLength = lengthEntry.joints * surfaceHeight;
      const hTrimQuantity = Math.ceil(totalHTrimLength / 10); // 10' stock length

      hTrims.push({
        productNumber: hTrimProduct.productNumber,
        productID: hTrimProduct.productID,
        name: hTrimProduct.name,
        color: hTrimProduct.color as ProductColor,
        quantity: hTrimQuantity,
        lengthFeet: hTrimProduct.lengthFeet,
        sqft: 0,
        type: 'H_Trim_Surface_Join',
      });
    }
  }

  const totalPanelCount = panels.reduce((sum, p) => sum + p.quantity, 0);

  return {
    panels,
    hTrims,
    totalPanelCount,
    totalJoints: lengthEntry.joints,
  };
}

/**
 * Find optimal panel length for cutting into multiple pieces, minimizing total material.
 * Used for short width/height surfaces where multiple cuts can come from a single panel.
 *
 * This function calculates the ACTUAL total material needed based on the number of cuts required,
 * rather than just optimizing waste-per-cut (which can lead to excess material).
 *
 * Example: 4' cuts × 6 rows needed
 * - 8' panels: 2 cuts each → 3 panels × 8' = 24' material (0 waste)
 * - 12' panels: 3 cuts each → 2 panels × 12' = 24' material (0 waste)
 * - 20' panels: 5 cuts each → 2 panels × 20' = 40' material (16' waste)
 * Result: 8' or 12' wins (same total), prefer shorter panel (8')
 *
 * @param cutLength - The length of each cut needed
 * @param totalCutsNeeded - Total number of cuts required
 * @returns [optimalPanelLength, physicalPanelsNeeded]
 */
function findOptimalPanelForTotalCuts(
  cutLength: number,
  totalCutsNeeded: number
): [number | null, number] {
  if (cutLength <= 0 || totalCutsNeeded <= 0) {
    return [null, 0];
  }

  let bestLength: number | null = null;
  let bestPanelsNeeded = Infinity;
  let minTotalMaterial = Infinity;

  // Try each available panel length
  for (const panelLength of PANEL_LENGTHS) {
    if (panelLength >= cutLength) {
      // Calculate how many cuts we can get from this panel
      const cutsPerPanel = Math.floor(panelLength / cutLength);
      // Calculate physical panels needed for the total cuts
      const panelsNeeded = Math.ceil(totalCutsNeeded / cutsPerPanel);
      // Calculate total material = panels × panel length
      const totalMaterial = panelsNeeded * panelLength;

      // Prefer solutions with less total material, then shorter panels (easier to handle)
      if (totalMaterial < minTotalMaterial ||
          (totalMaterial === minTotalMaterial && panelLength < (bestLength || Infinity))) {
        bestLength = panelLength;
        bestPanelsNeeded = panelsNeeded;
        minTotalMaterial = totalMaterial;
      }
    }
  }

  return [bestLength, bestPanelsNeeded];
}

// ============================================================================
// VERTICAL PANEL CALCULATIONS
// ============================================================================

export interface VerticalPanelResult {
  panels: MaterialItem[];
  hTrims: MaterialItem[];
  totalPanelCount: number;
}

/**
 * Find the optimal panel length for a given required height/length.
 *
 * Port of findOptimalPanelLengthAndFits_Helper from estimator.js
 *
 * For heights < 8' (wainscoting): Uses total material optimization to minimize waste
 * when multiple cuts can come from a single panel.
 *
 * For heights >= 8': Uses standard logic - shortest panel that fits with minimal waste.
 * This aligns with the length table approach used for horizontal panels.
 *
 * Examples (with 6 strips needed, height < 8'):
 * - 4' height: 8' panel → 2 cuts each, 3 panels = 24' total (best, shorter panel)
 * - 5' height: 10' panel → 2 cuts each, 3 panels = 30' total (best)
 *
 * Examples (height >= 8', standard logic):
 * - 9' height: 10' panel (1' waste per panel, shortest option)
 * - 10' height: 10' panel (0 waste, exact fit)
 *
 * @param requiredLength - The height/length to cover in feet
 * @param totalStripsNeeded - Total number of panel strips required (used only for < 8')
 * @returns [optimalPanelLength, numberOfFitsPerPanel]
 */
export function findOptimalPanelLength(
  requiredLength: number,
  totalStripsNeeded: number = 1
): [number | null, number] {
  if (requiredLength <= 0) {
    return [null, 0];
  }

  // Single-panel selection only. Sections > 20' need stacked panels, which
  // calculateVerticalPanelMix / getVerticalStripEntry handle; this fallback
  // returns the longest single panel.
  if (requiredLength > MAX_PANEL_LENGTH) {
    return [20, 1];
  }

  const MIN_LENGTH_FOR_OPTIMIZATION = 8;

  // For heights < 8': Use total material optimization (multiple cuts per panel)
  if (requiredLength < MIN_LENGTH_FOR_OPTIMIZATION) {
    let bestLength: number | null = null;
    let bestFits = 0;
    let minTotalMaterial = Infinity;

    for (const panelLength of PANEL_LENGTHS) {
      if (panelLength >= requiredLength) {
        const fitsPerPanel = Math.floor(panelLength / requiredLength);
        const panelsNeeded = Math.ceil(totalStripsNeeded / fitsPerPanel);
        const totalMaterial = panelsNeeded * panelLength;

        // Prefer less total material, then shorter panels
        if (totalMaterial < minTotalMaterial ||
            (totalMaterial === minTotalMaterial && panelLength < (bestLength || Infinity))) {
          bestLength = panelLength;
          bestFits = fitsPerPanel;
          minTotalMaterial = totalMaterial;
        }
      }
    }

    if (bestLength !== null) {
      return [bestLength, bestFits];
    }
  }

  // For heights >= 8': Use standard logic - shortest panel with minimal waste
  // Each panel covers one strip (1 fit per panel)
  let bestLength: number | null = null;
  let minWaste = Infinity;

  for (const panelLength of PANEL_LENGTHS) {
    if (panelLength >= requiredLength) {
      const waste = panelLength - requiredLength;

      // Prefer less waste, then shorter panels (shorter panels checked first in PANEL_LENGTHS)
      if (waste < minWaste) {
        bestLength = panelLength;
        minWaste = waste;
      }
    }
  }

  if (bestLength === null) {
    return [20, 1];
  }

  return [bestLength, 1]; // 1 fit per panel for heights >= 8'
}

/**
 * Panel lengths stacked in ONE vertical strip, bottom to top, for sections >= 8'.
 *
 * Mirrors the horizontal approach: the length table's combination for a run,
 * applied to the strip's height instead of the row's length. Uses the
 * fewest-seams variant (see getVerticalOptimalPanels). Returns the entry so
 * callers can read the joint count for H-trim.
 */
export function getVerticalStripEntry(sectionHeight: number): LengthTableEntry {
  return getVerticalOptimalPanels(Math.ceil(sectionHeight));
}

/**
 * Calculate vertical panel mix for a wall section.
 *
 * Port of calculateVerticalPanelMix_Helper from estimator.js
 *
 * - Heights < 8' (wainscoting): multiple cuts per panel via total-material optimization.
 * - Heights >= 8': the strip's panel combination (one panel per strip up to 20',
 *   stacked panels above 20') multiplied by the number of strips.
 *
 * @param requiredHeight - Height to cover in feet
 * @param totalPanelsNeeded - Number of panel strips needed
 * @returns Array of panel configurations
 */
export function calculateVerticalPanelMix(
  requiredHeight: number,
  totalPanelsNeeded: number
): Array<{ length: number; quantity: number; fitsPerPanel: number }> {
  if (requiredHeight <= 0 || totalPanelsNeeded <= 0) {
    return [];
  }

  if (requiredHeight >= 8) {
    const entry = getVerticalStripEntry(requiredHeight);
    const mix: Array<{ length: number; quantity: number; fitsPerPanel: number }> = [];
    for (const panelLength of WCB_PANEL_LENGTHS) {
      const count = entry[String(panelLength) as keyof LengthTableEntry] as number;
      if (count > 0) {
        mix.push({ length: panelLength, quantity: count * totalPanelsNeeded, fitsPerPanel: 1 });
      }
    }
    return mix;
  }

  // Pass totalPanelsNeeded to optimize for minimum total material
  const [optimalLength, fitsPerPanel] = findOptimalPanelLength(requiredHeight, totalPanelsNeeded);

  if (optimalLength && fitsPerPanel > 0) {
    const physicalPanelsNeeded = Math.ceil(totalPanelsNeeded / fitsPerPanel);

    return [
      {
        length: optimalLength,
        quantity: physicalPanelsNeeded,
        fitsPerPanel,
      },
    ];
  }

  return [];
}

/**
 * Calculate W&CB panels for vertical wall orientation.
 *
 * @param wallNetLength - Net wall length after garage deduction
 * @param sectionHeight - Height of the section to cover
 * @param color - Panel color
 * @param sectionType - Type of section (BelowSW, AboveSW, etc.)
 * @returns Panels needed for this section
 */
export function calculateVerticalPanels(
  wallNetLength: number,
  sectionHeight: number,
  color: ProductColor,
  sectionType: string
): MaterialItem[] {
  const panels: MaterialItem[] = [];

  if (wallNetLength <= 0 || sectionHeight <= 0) {
    return panels;
  }

  // Calculate number of vertical panel strips needed
  const numPanelStrips = calculatePanelStripsForWidth(wallNetLength);

  // Find optimal panel length for this height
  const panelMix = calculateVerticalPanelMix(sectionHeight, numPanelStrips);

  for (const mix of panelMix) {
    const product = getWCBProduct(mix.length as 8 | 10 | 12 | 14 | 16 | 20, color);
    if (product) {
      panels.push({
        productNumber: product.productNumber,
        productID: product.productID,
        name: product.name,
        color: product.color as ProductColor,
        quantity: mix.quantity,
        lengthFeet: product.lengthFeet,
        sqft: product.sqft,
        type: `WCB_Vertical_${sectionType}`,
      });
    }
  }

  return panels;
}

/**
 * Calculate H-trim needed for vertical stacking when height exceeds max panel length.
 *
 * When wall height > 20' (max panel length), panels must be stacked vertically
 * and H-trim is required at each horizontal joint between stacked panels.
 * Mirrors the horizontal rule turned sideways: joints come from the strip's
 * panel combination, and each joint spans the full wall length once.
 *
 * @param sectionHeight - Height of the wall section in feet
 * @param wallNetLength - Net length of the wall in feet
 * @param color - Panel color
 * @returns H-trim materials for vertical stacking
 */
export function calculateVerticalStackingHTrims(
  sectionHeight: number,
  wallNetLength: number,
  color: ProductColor
): MaterialItem[] {
  const hTrims: MaterialItem[] = [];

  if (sectionHeight <= MAX_PANEL_LENGTH || wallNetLength <= 0) {
    return hTrims; // No stacking needed
  }

  const numHorizontalJoints = getVerticalStripEntry(sectionHeight).joints;

  if (numHorizontalJoints <= 0) {
    return hTrims;
  }

  // H-trim runs horizontally across the full wall length at each joint
  const totalHTrimLength = numHorizontalJoints * wallNetLength;
  const hTrimQuantity = Math.ceil(totalHTrimLength / 10); // 10' stock length

  const hTrimProduct = getHTrimProduct(color);
  if (hTrimProduct && hTrimQuantity > 0) {
    hTrims.push({
      productNumber: hTrimProduct.productNumber,
      productID: hTrimProduct.productID,
      name: hTrimProduct.name,
      color: hTrimProduct.color as ProductColor,
      quantity: hTrimQuantity,
      lengthFeet: hTrimProduct.lengthFeet,
      sqft: 0,
      type: 'H_Trim_Vertical_Stack',
    });
  }

  return hTrims;
}

/**
 * Calculate all vertical wall panels including sections below/above SlatWall
 * and above garage doors.
 *
 * @param coreDims - Core dimensions for the wall
 * @param color - Panel color
 * @param garageConfig - Garage door configuration
 * @returns All panels needed for vertical wall
 */
export function calculateVerticalWallPanels(
  coreDims: CoreDimensions,
  color: ProductColor,
  garageConfig?: GarageConfig
): VerticalPanelResult {
  const panels: MaterialItem[] = [];
  const hTrims: MaterialItem[] = [];

  // Section 1: W&CB below SlatWall
  if (coreDims.wallheightBelowSW > 0 && coreDims.wallNetLength > 0) {
    const belowSWPanels = calculateVerticalPanels(
      coreDims.wallNetLength,
      coreDims.wallheightBelowSW,
      color,
      'BelowSW'
    );
    panels.push(...belowSWPanels);

    // Check for vertical stacking H-trim (height > 20')
    const stackingHTrims = calculateVerticalStackingHTrims(
      coreDims.wallheightBelowSW,
      coreDims.wallNetLength,
      color
    );
    hTrims.push(...stackingHTrims);
  }

  // Section 2: W&CB above SlatWall
  if (coreDims.wallheightAboveSW > 0 && coreDims.wallNetLength > 0) {
    const aboveSWPanels = calculateVerticalPanels(
      coreDims.wallNetLength,
      coreDims.wallheightAboveSW,
      color,
      'AboveSW'
    );
    panels.push(...aboveSWPanels);

    // Check for vertical stacking H-trim (height > 20')
    const stackingHTrims = calculateVerticalStackingHTrims(
      coreDims.wallheightAboveSW,
      coreDims.wallNetLength,
      color
    );
    hTrims.push(...stackingHTrims);
  }

  // Section 3: W&CB above garage door
  if (garageConfig?.enabled && coreDims.heightAboveGarage > 0) {
    const garageWidth = garageConfig.width * garageConfig.numDoors;
    const aboveGaragePanels = calculateVerticalPanels(
      garageWidth,
      coreDims.heightAboveGarage,
      color,
      'AboveGarage'
    );
    panels.push(...aboveGaragePanels);

    // Note: Above garage section unlikely to exceed 20', but check anyway
    const stackingHTrims = calculateVerticalStackingHTrims(
      coreDims.heightAboveGarage,
      garageWidth,
      color
    );
    hTrims.push(...stackingHTrims);
  }

  const totalPanelCount = panels.reduce((sum, p) => sum + p.quantity, 0);

  return {
    panels,
    hTrims,
    totalPanelCount,
  };
}

// ============================================================================
// HELPER IMPORTS (avoiding circular dependencies)
// ============================================================================

import { getHTrimProduct } from '../data/productTable';

export default {
  calculateHorizontalPanels,
  calculateVerticalPanels,
  calculateVerticalWallPanels,
  calculateVerticalStackingHTrims,
  findOptimalPanelLength,
  calculateVerticalPanelMix,
  getVerticalStripEntry,
};
